import cn from 'classnames';
import { Link } from '@nespresso-design-system/components';
import { getProductImage, type Product } from '@/domain/Product.ts';
import styles from './CapsuleQuickViewContent.module.scss';
import Tags from '../Tags/Tags.tsx';
import ProductImage from '@/widgets/ProductList/cards/parts/ProductImage/ProductImage.tsx';
import QuickViewHeadingGroup from '../QuickViewHeadingGroup/QuickViewHeadingGroup.tsx';
import VertuoOversizedCapsuleCompatibilitySection from '../VertuoOversizedCapsuleCompatibilitySection/VertuoOversizedCapsuleCompatibilitySection.tsx';
import AromaticNotes from '../AromaticNotes/AromaticNotes.tsx';
import CoffeeCupSizesAndRecipes from '../CoffeeCupSizesAndRecipes/CoffeeCupSizesAndRecipes.tsx';
import Roast from '../Roast/Roast.tsx';
import Origins from '../Origins/Origins.tsx';
import ContentsAndAllergens from '../ContentsAndAllergens/ContentsAndAllergens.tsx';
import CoffeeIntensity from '../CoffeeIntensity/CoffeeIntensity.tsx';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import type { Options } from '@/widgets/ProductList/config';
import FlavourProfile from '../FlavourProfile/FlavourProfile.tsx';
import BundleProductsList from '../BundleProductsList/BundleProductsList.tsx';
import useABTestVariant from '@/widgets/hooks/useABTestVariant.tsx';
import EXPERIMENTS from '@/constants/abExperiments.ts';

export type CapsuleQuickViewContentProps = {
  product: Product;
  pdpLinkText: string;
  onGoToProductPage: (event: MouseEvent) => void;
};
export default function CapsuleQuickViewContent({
  product,
  pdpLinkText,
  onGoToProductPage
}: CapsuleQuickViewContentProps) {
  const { hideIntensityMeterQuickView } = useWidgetOptions<Options>();
  const galleryImage = getProductImage(product, 'mediaGallery');
  const isAlternateVariant = useABTestVariant(
    EXPERIMENTS.QUICK_VIEW_DRAWER.ALTERNATE
  );
  const image = getProductImage(product, 'main');
  const { bundle, groupedProducts } = product;
  const isBundledOrGrouped =
    (bundle?.contents?.length ?? 0) > 0 || (groupedProducts?.length ?? 0) > 0;

  return (
    <div
      class={styles.modalInner}
      tabIndex={0}
      role="region"
      aria-label={product.name}
      data-product-long-sku={product.id}
      data-product-short-sku={product.nessoft?.legacyId}
    >
      <Tags product={product} />

      {/* TODO - make ProductImage support srcsets? */}
      <ProductImage
        image={galleryImage ?? image}
        width={200}
        imagePolicy={{
          policyId: 'medium',
          targetWidth: 200,
          density: 1
        }}
        class={cn(
          styles.productImage,
          isBundledOrGrouped && styles.bundleImage // make bundle images bigger
        )}
        loading="lazy"
      />

      <QuickViewHeadingGroup product={product} class={styles.nameHeadline} />
      {!hideIntensityMeterQuickView && <CoffeeIntensity product={product} />}

      {/* A/B Test variant */}
      {isAlternateVariant && pdpLinkText && (
        <div class={styles.pdpLink}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link onClick={onGoToProductPage} label={pdpLinkText} type="button" />
        </div>
      )}

      {isBundledOrGrouped ? (
        <BundleProductsList product={product} />
      ) : (
        <>
          <VertuoOversizedCapsuleCompatibilitySection product={product} />
          <div class={styles.specsGroup}>
            <FlavourProfile product={product} />
            <AromaticNotes product={product} />
            <CoffeeCupSizesAndRecipes product={product} />
            <Roast product={product} />
            <Origins product={product} />
            <ContentsAndAllergens product={product} />
          </div>
        </>
      )}
    </div>
  );
}
